import type { AfterViewInit, OnDestroy } from '@angular/core';

import { Directive, ElementRef, EventEmitter, HostListener, Input, Output, inject, DOCUMENT } from '@angular/core';

import { fromEvent, Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[spClickOutside]',
    standalone: true,
})
export class SpClickOutsideDirective implements AfterViewInit, OnDestroy {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly document = inject<Document>(DOCUMENT);

    @Input() public clickClassClosePrevent: string = 'click-outside-close-prevent';
    @Input() public rootClass: string = 'constructor-content';
    @Output() public clickOutside: EventEmitter<void> = new EventEmitter();

    private readonly unsubscribe$ = new Subject<void>();

    public ngAfterViewInit(): void {
        fromEvent<MouseEvent>(this.document, 'click')
            .pipe(takeUntil(this.unsubscribe$), skip(1))
            .subscribe((event: MouseEvent) => {
                const target = event.target as HTMLElement;

                if (
                    target.closest(`.${this.clickClassClosePrevent}`) || // prevent close on click on element with class
                    target?.classList?.contains('fm-backdrop') || // prevent close on click on file manager backdrop
                    this.isFileManagerOpened() || // prevent close on click on file manager overlay
                    this.isFileManagerOverlayContainsTarget(target) // prevent close on click on file manager overlay
                ) {
                    return;
                }

                if (target.shadowRoot) {
                    if (!event.composedPath().includes(this.elementRef.nativeElement)) {
                        this.clickOutside.emit();
                        return;
                    }
                }
                if (target.closest(`.${this.rootClass}`)) {
                    this.clickOutside.emit();
                }
            });
    }

    @HostListener('window:keydown.escape')
    public onEscapeDownHandler(): void {
        if (this.isFileManagerOpened() || this.isModalOpened()) {
            return;
        }

        this.clickOutside.emit();
    }

    private isFileManagerOpened(): boolean | undefined {
        return this.document.querySelector('.fm-backdrop')?.classList?.contains('fm-backdrop--open');
    }

    private isFileManagerOverlayContainsTarget(target: HTMLElement): boolean | undefined {
        return this.document.querySelector('fm-overlay')?.contains(target);
    }

    private isModalOpened(): boolean {
        return Boolean(this.document.querySelector('modal-container'));
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
